import {
  DeviceScannerEmitter,
  PrinterNativeModule,
} from '../components/PrinterNativeModule';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  checkBluetoothPermissions,
  requestBluetoothPermissions,
} from '../shared/PrinterSetting/common';
import {getClientType, getStoreName} from '@buncha/common/types/common';
import {useAppDispatch, useAppSelector} from '@buncha/config/redux/hooks';
import {
  useGetMasterRunForDriver,
  useVanLoadAtReducer,
} from '../hooks/VanLoadAt';
import {useIsFocused, useRoute} from '@react-navigation/native';

import AlertBox from '@buncha/batchDetails/component/AlertBox/AlertBox';
import {Analytics} from '@buncha/config/analytics/analytics';
import {BluetoothDevice} from '../types/PrinterSettings';
import {BottomButton} from '@buncha/components/composites/Button';
import {Box} from '@buncha/components/core/view';
import BuyerOrderList from '../components/vanLoadAt/BuyerOrderList';
import {DeliveryLocationStatus} from '../types/common';
import {Divider} from 'native-base';
import {EventType} from '@buncha/config/analytics/type';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '@buncha/components/conditional/If';
import LoaderComponents from '@buncha/components/composites/CustomLoader/LoaderComponents';
import {Modules} from '@buncha/utils/types';
import PrinterSettingBottomSheet from '../components/vanLoadAt/BuyerOrderList/PrinterSettingBottomSheet';
import SequenceBottomSheet from '../components/vanLoadAt/BuyerOrderList/SequenceBottomSheet';
import {SpinnerComponent} from '@buncha/components/core/loader';
import {StorageKeys} from '@buncha/config/storage/type';
import {StyleSheet} from 'react-native';
import {VanLoadAtContext} from '../context/VanLoadAt';
import VanLoadAtHeader from '../components/vanLoadAt/VanLoadAtHeader';
import {driverActions} from '../reducer';
import {getOs} from '@buncha/utils/appInfo';
import {homeActions} from '@buncha/home/reducer';
import {isWeb} from '@buncha/utils/common';
import onfleetService from '@buncha/config/onfleet';
import {storage} from '@buncha/config/storage';
import {uniqBy} from 'lodash';
import {useColorThemeType} from '@buncha/appStyles/hooks/theme';
import {useIsMeijerStore} from '@buncha/home/hooks/home';
import {useGetLocale} from '@buncha/localization/hooks';

const LoadVanAt = () => {
  const theme = useColorThemeType('main');
  const [state, dispatch] = useVanLoadAtReducer();
  const provider = useMemo(() => ({state, dispatch}), [dispatch, state]);
  const [isBluetoothEnabled, setIsBluetoothEnabled] = useState(false);
  const [deviceList, setDeviceList] = useState<any>([]);
  const [isScanning, setIsScanning] = useState(false);

  const bleDevice = useAppSelector(gbState => gbState.driver.BLEDevice);
  const [hasDeviceSelected, setHasDeviceSelected] = useState(false);
  const sortedTasks = useAppSelector(gState => gState.home.driverTasks) ?? [];
  const appDispatch = useAppDispatch();
  const {body, button} = useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};

  const route = useRoute();
  const routeParams: {runId?: number} | undefined = route.params;
  const runId = routeParams?.runId ?? 0;
  const isFocused = useIsFocused();

  const run = state?.run;
  const masterRun = run?.masterRun;
  const masterStore = masterRun?.store;
  const storeId = masterStore?.id;
  const isMeijerStore = useIsMeijerStore(storeId);

  const [sequanceModal, setSequanceModal] = useState(false);
  const [hasPermissionStatus, setHasPermissionStatus] = useState(true);
  const [printerSettingModal, setPrinterSettingModal] = useState(false);
  const deviceScanner = PrinterNativeModule.DeviceScanner;
  const rnPrinter = PrinterNativeModule.RNPrinter;

  const toggleBottomSheet = useCallback(() => {
    setSequanceModal(!sequanceModal);
  }, [sequanceModal]);

  const togglePrinterBottomSheet = useCallback(() => {
    setPrinterSettingModal(!printerSettingModal);
  }, [printerSettingModal]);

  const {getMasterRun, loading} = useGetMasterRunForDriver(dispatch);

  const onPressHandler = useCallback(() => {
    Analytics.eventWithProps('Start route', {
      type: EventType.cta,
      runId: runId,
      source: 'Van Load Page',
      client: getClientType(isMeijerStore),
      storeType: getStoreName(String(storeId)),
    });
    toggleBottomSheet();
  }, [isMeijerStore, runId, storeId, toggleBottomSheet]);

  const handleBluetoothStateChange = useCallback((isEnabled: boolean) => {
    setIsBluetoothEnabled(isEnabled);
  }, []);

  useEffect(() => {
    if (getOs() === 'android') {
      requestBluetoothPermissions();
      DeviceScannerEmitter?.addListener('DeviceFound', setDeviceList);
      DeviceScannerEmitter?.addListener('ScanningStatus', setIsScanning);
    }
    const deviceOnOffListener = DeviceScannerEmitter?.addListener(
      'DeviceOnOff',
      handleBluetoothStateChange,
    );

    return () => {
      DeviceScannerEmitter?.removeAllListeners('DeviceFound');
      DeviceScannerEmitter?.removeAllListeners('ScanningStatus');
      deviceOnOffListener?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkPermissionsHandler = useCallback(async () => {
    const status = await checkBluetoothPermissions();
    setHasPermissionStatus(status);
  }, []);

  const getDeviceList = useCallback(async () => {
    const list = await rnPrinter.ScanDevice(2);
    const uniqueList = uniqBy(list, 'address');
    setDeviceList(uniqueList);
  }, [rnPrinter]);

  useEffect(() => {
    let isBTEnabled = true;
    if (runId && isFocused && !isWeb()) {
      deviceScanner.isBluetoothEnabled((isEnabled: boolean) => {
        setIsBluetoothEnabled(isEnabled);
        isBTEnabled = isEnabled;
      });
      if (getOs() === 'android') {
        checkPermissionsHandler();
        if (isBTEnabled) deviceScanner.enableScan();
      } else if (getOs() === 'ios') {
        getDeviceList();
      }
    }
    getMasterRun(runId);

    Analytics.eventWithProps('Van Load At', {
      type: EventType.page,
      runId: runId,
      client: getClientType(isMeijerStore),
      storeType: getStoreName(String(storeId)),
    });

    onfleetService.fetchTasks().then(response => {
      appDispatch(homeActions.setDriverTasks(response));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, isFocused]);

  useEffect(() => {
    if ((deviceList.length && !isScanning) || !hasPermissionStatus) {
      let isDeviceFound = false;
      deviceList.map((device: BluetoothDevice) => {
        if (
          device?.address === bleDevice?.address &&
          (getOs() === 'andrdoid' ? device.isPaired : true)
        ) {
          isDeviceFound = true;
          setHasDeviceSelected(true);
        }
      });

      if (!isDeviceFound) setHasDeviceSelected(false);
      // appDispatch(driverActions.setBLEDevice(undefined));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceList, isScanning]);

  useEffect(() => {
    if (bleDevice?.address) setHasDeviceSelected(true);
  }, [bleDevice]);

  const updateBLEDeviceFromStorage = useCallback(async () => {
    if (!bleDevice) {
      const device = await storage.getItem(StorageKeys.BLEDevice);
      if (device) appDispatch(driverActions.setBLEDevice(JSON.parse(device)));
    }
  }, [bleDevice, appDispatch]);

  useEffect(() => {
    updateBLEDeviceFromStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bleDevice]);

  const disabledButton = useMemo(() => {
    let hasEnabled = false;
    if (state.groupedOrders?.length)
      state.groupedOrders.forEach(groupedOrder => {
        groupedOrder.orders.forEach(order => {
          const orderStatus = order.deliveryLocation.deliveryStatus;
          if (orderStatus !== DeliveryLocationStatus.driverAssigned)
            hasEnabled = true;
        });
      });
    else hasEnabled = true;
    return hasEnabled;
  }, [state.groupedOrders]);

  const getDeviceSelectAndBTStatus = useMemo(
    () => hasDeviceSelected && isBluetoothEnabled,
    [hasDeviceSelected, isBluetoothEnabled],
  );

  return (
    <VanLoadAtContext.Provider value={provider}>
      <If condition={loading}>
        <LoaderComponents showBottomText={false} />
      </If>
      <If condition={!loading}>
        <Box
          style={[
            styles.flexBox,
            {backgroundColor: theme?.background?.white?.[100]},
          ]}>
          <VanLoadAtHeader
            storeLogoUrl={masterStore?.storeLogoUrl ?? ''}
            storeName={masterStore?.name ?? ''}
            address={masterStore?.address ?? ''}
            startRoute={masterRun?.dropOffStartTime ?? ''}
          />

          <Box style={styles.alertBox}>
            <AlertBox title={body?.alert} />
          </Box>

          <BuyerOrderList
            sortedTasks={sortedTasks}
            runId={runId}
            infoButtonPress={toggleBottomSheet}
            printerSettingsModalHandler={togglePrinterBottomSheet}
            isBluetoothEnabled={getDeviceSelectAndBTStatus}
            hasDeviceSelected={
              getOs() === 'android'
                ? getDeviceSelectAndBTStatus && hasPermissionStatus
                : getDeviceSelectAndBTStatus
            }
          />
        </Box>
        <Box style={styles.bottomButtonBox}>
          <Divider />
          <BottomButton
            title={button?.startRoute}
            disabled={disabledButton}
            onPress={onPressHandler}
            isLoading={false}
            loadingIndicator={<SpinnerComponent />}
          />
        </Box>
      </If>
      <If condition={sequanceModal}>
        <SequenceBottomSheet
          open={sequanceModal}
          onClose={toggleBottomSheet}
          runId={runId}
          routeStarted={!disabledButton}
        />
      </If>
      <PrinterSettingBottomSheet
        open={printerSettingModal}
        onClose={togglePrinterBottomSheet}
      />
    </VanLoadAtContext.Provider>
  );
};

export default React.memo(LoadVanAt);

export const styles = StyleSheet.create({
  flexBox: {
    flex: 1,
  },
  bottomButtonBox: {marginBottom: 10},
  banner: {
    marginBottom: 10,
    alignSelf: 'center',
  },
  alertBox: {
    marginTop: 15,
    paddingLeft: 10,
    paddingRight: 20,
  },
});
